import * as THREE from 'three';
import type { DistrictKind } from '@somnhold/content';
import {
  buildTerrain,
  districtAt,
  districtCenter,
  hexDist,
  cellCenter,
  cellId,
  gridW,
  hexLen,
  hexToWorld,
  T_GATE,
  T_INTERIOR,
  T_KEEP,
  T_PATH,
  T_PLAZA,
  T_RAMPART,
  T_SPAWN,
  T_VOID,
  type MapDef,
} from '@somnhold/sim';
import { WORLD_PER_HEX } from './coords.ts';
import { buildOutskirts, type FogBank } from './outskirts.ts';

/** Small seeded PRNG for decor placement (render-side, not the sim one). */
export const mulberry = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const hash2 = (q: number, r: number): number => {
  let h = Math.imul(q * 374761393 + r * 668265263, 0x27d4eb2d);
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
};

/** Height of a cell's top surface in world units. */
export const cellHeight = (t: number): number => {
  switch (t) {
    case T_INTERIOR:
      return 0;
    case T_PLAZA:
      return 0.03;
    case T_KEEP:
      return 0.06;
    case T_PATH:
    case T_SPAWN:
      return -0.1;
    case T_GATE:
      return -0.02;
    case T_RAMPART:
      return 0.04;
    default:
      // Outside the tiles the outskirts' continuous ground takes over (outskirts.ts).
      return -0.12;
  }
};

const C = {
  interiorA: new THREE.Color('#5c605e'),
  interiorB: new THREE.Color('#666a66'),
  moss: new THREE.Color('#4c5a3a'),
  plaza: new THREE.Color('#85878a'),
  keep: new THREE.Color('#77797c'),
  gate: new THREE.Color('#4f4b44'),
  rampart: new THREE.Color('#57534b'),
  side: new THREE.Color('#25262a'),
};

/**
 * Each kind of district is paved in its own stone and pattern, so it reads from the far camera while
 * every cell keeps its grout line: drill-yard stripes of dark basalt (military), rough dusty slabs
 * (quarry), pale blue-white flags with rings of inlay round the centre (sacred) and a three-tone
 * mosaic of teal slate (guild).
 */
export const DISTRICT_PAVING: Record<DistrictKind, { a: string; b: string; c: string; inlay: string }> = {
  military: { a: '#47494f', b: '#5a5c63', c: '#52545a', inlay: '#3c3d43' },
  quarry: { a: '#6a6966', b: '#74736f', c: '#7e7d79', inlay: '#5c5c5a' },
  sacred: { a: '#69717c', b: '#6e7681', c: '#737b86', inlay: '#79828e' },
  guild: { a: '#55625f', b: '#5e6c69', c: '#4c5960', inlay: '#3f4b4a' },
};

/** Bands of a tile's top from the centre out: up to `r` (fraction of the tile) in `color`. */
type Band = { r: number; color: THREE.Color };

export interface TerrainBuild {
  mesh: THREE.Mesh;
  terrain: number[];
  heights: Float32Array;
  decor: THREE.Group;
  /** Continuous ground height at a world XZ point outside the hex tiles (smooth relief, roads). */
  ground?: (x: number, z: number) => number;
  /** Distance from a world XZ point to the nearest road (outskirts only). */
  roadDist?: (x: number, z: number) => number;
  /** Drifting fog over the edge of the map (animated every frame). */
  fog?: FogBank;
}

/**
 * Builds the diorama: one merged mesh of hex prisms with painted vertex colors for the cells
 * inside the walls (or the clearing), and the outskirts beyond them: continuous ground with roads,
 * woods and the fog bank at the edge (see outskirts.ts). The hex grid only shows where you build.
 */
export const buildTerrainMesh = (map: MapDef, seed: number, opts: { low?: boolean } = {}): TerrainBuild => {
  const R = map.radius;
  const terrain = buildTerrain(map);
  const out = buildOutskirts(map, seed, opts.low ?? false);
  const W = gridW(map.radius);
  const heights = new Float32Array(W * W);
  const pos: number[] = [];
  const col: number[] = [];
  const S = WORLD_PER_HEX;
  const rad = (S / Math.sqrt(3)) * 1.0; // circumradius of a pointy hex with width S
  const corners: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 30);
    corners.push([Math.cos(a) * rad, Math.sin(a) * rad]);
  }
  const tmp = new THREE.Color();
  /** Old-ring cells now inside a district (rubble of the foundation is strewn along them). */
  const trace: { q: number; r: number; h: number }[] = [];
  const pushTri = (a: number[], b: number[], c: number[], ca: THREE.Color, cb: THREE.Color, cc: THREE.Color): void => {
    pos.push(...a, ...b, ...c);
    col.push(ca.r, ca.g, ca.b, cb.r, cb.g, cb.b, cc.r, cc.g, cc.b);
  };
  for (let r = -R; r <= R; r++) {
    for (let q = -R; q <= R; q++) {
      const d = hexLen(q, r);
      if (d > R) continue;
      const id = cellId(map.radius, q, r);
      const t = id >= 0 ? (terrain[id] as number) : T_VOID;
      const c = hexToWorld(q, r);
      const cx = (c.x / 1000) * S;
      const cz = (c.y / 1000) * S;
      if (t === T_VOID || t === T_PATH || t === T_SPAWN) {
        // Outside the walls: the outskirts' continuous ground, no tile.
        if (id >= 0) heights[id] = out.ground(cx, cz);
        continue;
      }
      const h = cellHeight(t);
      if (id >= 0) heights[id] = h;
      const n = hash2(q, r);
      let top: THREE.Color;
      let bands: Band[] = [];
      /** Per-corner height jitter of the top (rough slabs). */
      let rough = 0;
      let inset = 0.97;
      switch (t) {
        case T_INTERIOR: {
          inset = 0.965;
          const dist = map.kind === 'bastion' ? districtAt(map, q, r) : undefined;
          if (!dist) {
            top = tmp.copy(n < 0.5 ? C.interiorA : C.interiorB).lerp(C.moss, n * n * 0.6);
            break;
          }
          const pv = DISTRICT_PAVING[dist.kind];
          const dc = districtCenter(map.bastion, dist.side);
          const dd = hexDist(dc, { q, r });
          const n2 = hash2(q * 3 + 11, r * 5 - 7);
          switch (dist.kind) {
            case 'military':
              // Dark basalt in stripes across the drill yard.
              top = tmp.set(Math.abs(r - dc.r) % 2 === 0 ? pv.a : pv.b).multiplyScalar(0.97 + n2 * 0.06);
              break;
            case 'quarry':
              // Rough slabs under stone dust, set unevenly.
              top = tmp.set(n < 0.33 ? pv.a : n < 0.66 ? pv.b : pv.c).multiplyScalar(0.9 + n2 * 0.18);
              rough = 0.03;
              break;
            case 'sacred':
              // Rings of pale inlay round the heart of the precinct, a star at its centre.
              top = tmp.set(n < 0.5 ? pv.a : pv.b);
              if (dd === 0) bands = [{ r: 0.26, color: new THREE.Color('#99a6b8') }, { r: 0.58, color: new THREE.Color(pv.inlay) }];
              else if (dd % 2 === 0) bands = [{ r: 0.56, color: top.clone() }, { r: 0.62, color: new THREE.Color(pv.inlay) }];
              break;
            case 'guild':
              // The three-tone mosaic of the guild halls.
              top = tmp.set([pv.a, pv.b, pv.c][(((q - r) % 3) + 3) % 3] as string).multiplyScalar(0.97 + n2 * 0.06);
              break;
          }
          // Where the old ring stood before the district took it in: its foundation, worn into the paving.
          if (hexLen(q, r) === map.bastion) {
            top.lerp(C.rampart, 0.55);
            bands = [{ r: 0.55, color: top.clone().multiplyScalar(1.06) }];
            trace.push({ q, r, h: 0 });
          }
          break;
        }
        case T_PLAZA:
          top = tmp.copy(C.plaza).multiplyScalar(0.92 + n * 0.12);
          inset = 0.97;
          break;
        case T_KEEP:
          top = tmp.copy(C.keep);
          break;
        case T_GATE:
          top = tmp.copy(C.gate);
          inset = 1.0;
          break;
        case T_RAMPART:
          top = tmp.copy(C.rampart);
          inset = 1.0;
          break;
        default:
          top = tmp.copy(C.interiorA);
      }
      const topC = top.clone();
      // Slight per-vertex variation: hand-painted look.
      const vc = (k: number): THREE.Color => topC.clone().multiplyScalar(0.94 + hash2(q * 7 + k, r * 13 - k) * 0.12);
      const center = [cx, h, cz];
      const ring = corners.map(([x, z], i) => [cx + x * inset, h + (rough ? (hash2(q * 13 + i, r * 7 - i) - 0.5) * rough : 0), cz + z * inset]);
      if (bands.length === 0) {
        for (let i = 0; i < 6; i++) {
          const a = ring[i] as number[];
          const b = ring[(i + 1) % 6] as number[];
          pushTri(center, b, a, topC, vc(i + 1), vc(i));
        }
      } else {
        // Inlay: concentric bands, the outermost in the tile's own color (no overlap, no flicker).
        const all = [...bands, { r: 1, color: topC }];
        let prev: number[][] | null = null;
        all.forEach((band, bi) => {
          const k = band.r * inset;
          const ringB = corners.map(([x, z]) => [cx + x * k, h, cz + z * k]);
          const cc = band.color;
          const cv = (i: number): THREE.Color => (bi === all.length - 1 ? vc(i) : cc.clone().multiplyScalar(0.96 + hash2(q * 5 + i, r * 3 - i * bi) * 0.08));
          for (let i = 0; i < 6; i++) {
            const a = ringB[i] as number[];
            const b = ringB[(i + 1) % 6] as number[];
            if (!prev) pushTri(center, b, a, cc, cv(i + 1), cv(i));
            else {
              const pa = prev[i] as number[];
              const pb = prev[(i + 1) % 6] as number[];
              pushTri(pa, pb, b, cc, cc, cv(i + 1));
              pushTri(pa, b, a, cc, cv(i + 1), cv(i));
            }
          }
          prev = ringB;
        });
      }
      // Sides down to the base so height steps read as terraces.
      const bottom = Math.min(h, -0.2) - 0.6;
      const side = tmp.copy(C.side).lerp(topC, 0.35);
      const sideTop = side.clone();
      const sideBot = side.clone().multiplyScalar(0.45);
      for (let i = 0; i < 6; i++) {
        const a = ring[i] as number[];
        const b = ring[(i + 1) % 6] as number[];
        const a2 = [a[0] as number, bottom, a[2] as number];
        const b2 = [b[0] as number, bottom, b[2] as number];
        pushTri(a, b, b2, sideTop, sideTop, sideBot);
        pushTri(a, b2, a2, sideTop, sideBot, sideBot);
      }
      // Inset tiles leave a dark grout gap: fill it with a lower ring.
      if (inset < 1) {
        const outer = corners.map(([x, z]) => [cx + x, h - 0.03, cz + z]);
        const grout = topC.clone().multiplyScalar(0.62);
        for (let i = 0; i < 6; i++) {
          const a = ring[i] as number[];
          const b = ring[(i + 1) % 6] as number[];
          const ao = outer[i] as number[];
          const bo = outer[(i + 1) % 6] as number[];
          pushTri(a, b, bo, grout, grout, grout);
          pushTri(a, bo, ao, grout, grout, grout);
        }
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.computeVertexNormals();
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0, flatShading: true });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  mesh.name = 'terrain';
  const decor = buildDecor(map, terrain, heights, seed, trace);
  decor.add(out.group);
  return { mesh, terrain, heights, decor, ground: out.ground, roadDist: out.roadDist, fog: out.fog };
};

const buildDecor = (map: MapDef, terrain: number[], heights: Float32Array, seed: number, trace: { q: number; r: number }[]): THREE.Group => {
  const g = new THREE.Group();
  g.name = 'decor';
  const rnd = mulberry(seed ^ 0xdec0);
  const S = WORLD_PER_HEX;
  const R = map.radius;
  const tufts: THREE.Matrix4[] = [];
  const m = new THREE.Matrix4();
  const q4 = new THREE.Quaternion();
  const e = new THREE.Euler();
  // A few grass tufts between the flagstones inside the walls.
  for (let r = -R; r <= R; r++) {
    for (let q = -R; q <= R; q++) {
      const id = cellId(R, q, r);
      // New district paving is clean; grass only grows in the old bastion.
      if (id < 0 || terrain[id] !== T_INTERIOR || rnd() >= 0.12 || (map.kind === 'bastion' && hexLen(q, r) >= map.bastion)) continue;
      const c = cellCenter(R, id);
      const jitter = (): number => (rnd() - 0.5) * S * 0.63;
      e.set((rnd() - 0.5) * 0.4, rnd() * Math.PI * 2, (rnd() - 0.5) * 0.4);
      q4.setFromEuler(e);
      m.compose(new THREE.Vector3((c.x / 1000) * S + jitter(), heights[id] as number, (c.y / 1000) * S + jitter()), q4, new THREE.Vector3(0.05, 0.07, 0.05));
      tufts.push(m.clone());
    }
  }
  if (tufts.length > 0) {
    const im = new THREE.InstancedMesh(
      new THREE.ConeGeometry(0.5, 1, 5).translate(0, 0.5, 0),
      new THREE.MeshStandardMaterial({ color: '#4d5a2e', roughness: 1, flatShading: true }),
      tufts.length,
    );
    tufts.forEach((mm, i) => im.setMatrixAt(i, mm));
    im.instanceMatrix.needsUpdate = true;
    g.add(im);
  }
  // The old ring's foundation stones, left in the grout where it stood (off the cell centres).
  if (trace.length > 0) {
    const ms: THREE.Matrix4[] = [];
    const cs: THREE.Color[] = [];
    const rad = (S / Math.sqrt(3)) * 0.98;
    for (const t of trace) {
      const c = hexToWorld(t.q, t.r);
      for (let i = 0; i < 6; i++) {
        if (rnd() < 0.45) continue;
        const a = (Math.PI / 180) * (60 * i - 30) + (rnd() - 0.5) * 0.25;
        const s = 0.035 + rnd() * 0.04;
        e.set(rnd() * 3, rnd() * 3, rnd() * 3);
        q4.setFromEuler(e);
        m.compose(new THREE.Vector3((c.x / 1000) * S + Math.cos(a) * rad, 0.0, (c.y / 1000) * S + Math.sin(a) * rad), q4, new THREE.Vector3(s * 1.4, s * 0.7, s));
        ms.push(m.clone());
        cs.push(new THREE.Color().setHSL(0.6, 0.04, 0.2 + rnd() * 0.08));
      }
    }
    const im = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(1, 0), new THREE.MeshStandardMaterial({ roughness: 1, flatShading: true }), ms.length);
    ms.forEach((mm, i) => {
      im.setMatrixAt(i, mm);
      im.setColorAt(i, cs[i] as THREE.Color);
    });
    im.instanceMatrix.needsUpdate = true;
    im.name = 'old-ring';
    g.add(im);
  }
  return g;
};

export const terrainHeightAt = (b: TerrainBuild, map: MapDef, x: number, y: number): number => {
  // Sim fixed coords → nearest cell height.
  const qf = (x - (y * 1000) / 866 / 2) / 1000;
  const rf = y / 866;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(-qf - rf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - (-qf - rf));
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  const id = cellId(map.radius, q, r);
  const t = id >= 0 ? (b.terrain[id] as number) : T_VOID;
  // Outside the tiles the ground is continuous: sample it where the piece actually stands.
  if (b.ground && (t === T_VOID || t === T_PATH || t === T_SPAWN)) return b.ground((x / 1000) * WORLD_PER_HEX, (y / 1000) * WORLD_PER_HEX);
  return id >= 0 ? (b.heights[id] as number) : -0.3;
};
